import { useTranslate } from "@tolgee/react";
import { PercentageSlider } from "#/components/ui/percentage-slider.tsx";
import { DepositDialog } from "#/features/trade/components/order-form/deposit-dialog.tsx";
import { OrderSubmitButton } from "#/features/trade/components/order-form/order-submit-button.tsx";
import { cn } from "#/lib/utils";
import { useOrderFormSetup } from "../order-form/hooks/use-order-form-setup.ts";
import { LeverageDialog } from "../order-form/leverage-dialog.tsx";
import { MarginModeDialog } from "../order-form/margin-mode-dialog.tsx";
import { OrderAccountSummary } from "../order-form/order-account-summary.tsx";
import { OrderFormFields } from "../order-form/order-form-fields.tsx";
import { OrderFormMetrics } from "../order-form/order-form-metrics.tsx";
import { OrderFormOptions } from "../order-form/order-form-options.tsx";
import { OrderSideToggle } from "../order-form/order-side-toggle.tsx";
import { MobileOrderTypeTabs } from "./mobile-order-type-tabs.tsx";

export function MobileOrderForm() {
	const { t } = useTranslate();
	const {
		wallet,
		marketData,
		form,
		quickPrice,
		marginCapability,
		updateLeverage,
		depositAction,
		placeOrder,
	} = useOrderFormSetup();

	const mobileControlClassName =
		"h-5.5 min-h-0 flex-none justify-center border-0 !bg-transparent px-0 px-10 py-0 text-caption1-medium text-trade-text-strong !shadow-none hover:!bg-transparent hover:text-trade-text-strong dark:!bg-transparent sm:h-6";

	return (
		<div className="flex h-full flex-col gap-1 p-2 sm:gap-2 sm:p-3.5 bg-trade-surface">
			<div
				className={cn(
					"grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(44px,auto)] items-center border-b border-trade-border-subtle",
					"gap-1.5 pb-0.5 sm:gap-2 sm:pb-1",
				)}
			>
				<MobileOrderTypeTabs
					value={form.orderType}
					onValueChange={form.setOrderType}
				/>
				<MarginModeDialog
					coin={marketData.coin}
					value={form.marginMode}
					onChange={form.setMarginMode}
					currentLeverage={form.selectedLeverage}
					hasAvailableFunds={form.hasAvailableFunds}
					updateLeverage={updateLeverage}
					marginCapability={marginCapability}
					triggerClassName={mobileControlClassName}
				/>
				<LeverageDialog
					coin={marketData.coin}
					value={form.selectedLeverage}
					selectedIndex={form.selectedLeverageIndex}
					leverageMarks={form.leverageMarks}
					maxLeverage={marketData.maxLeverage}
					marginMode={form.marginMode}
					supportsCrossMargin={marginCapability.supportsCrossMargin}
					hasAvailableFunds={form.hasAvailableFunds}
					onChange={form.setSelectedLeverageIndex}
					updateLeverage={updateLeverage}
					triggerClassName={mobileControlClassName}
				/>
			</div>

			{/* Buy / Sell toggle */}
			<OrderSideToggle side={form.side} onChange={form.setSide} />

			{/* Account summary */}
			<OrderAccountSummary
				availableFunds={form.availableFunds}
				currentPositionLabel={form.currentPositionLabel}
			/>

			{/* Form fields (no percentage slider) */}
			<OrderFormFields
				form={form}
				coin={marketData.coin}
				szDecimals={marketData.szDecimals ?? 4}
				quickPrice={quickPrice}
				showPercentageSlider={false}
			/>

			<div className="px-1">
				<PercentageSlider
					value={form.amountPercentage}
					onChange={form.setAmountPercentage}
				/>
			</div>

			{/* Order metrics */}
			<OrderFormMetrics
				estimatedLiquidation={form.estimatedLiquidation}
				marginRequired={form.marginRequired}
			/>

			<OrderFormOptions
				reduceOnly={form.reduceOnly}
				onReduceOnlyChange={form.setReduceOnly}
				autoQuoteAvailable={form.autoQuoteAvailable}
				showAutoQuote={form.showLimitPrice}
				autoQuote={form.autoQuote}
				onAutoQuoteChange={form.setAutoQuote}
				showTimeInForce={form.showTimeInForce}
				timeInForce={form.timeInForce}
				onTimeInForceChange={form.setTimeInForce}
			/>

			{/* Submit button or deposit prompt */}
			{wallet.isConnected && !form.hasAvailableFunds ? (
				<DepositDialog
					onConfirm={(amount) => depositAction.mutateAsync(amount)}
				>
					<button
						type="button"
						className="flex min-h-9 w-full items-center justify-center rounded-[8px] bg-primary/10 text-label2-bold text-primary transition-colors hover:bg-primary/16 sm:min-h-10.5 sm:rounded-[9px] sm:text-label1-bold"
					>
						{t("form.deposit-to-trade")}
					</button>
				</DepositDialog>
			) : (
				<OrderSubmitButton
					submitLabel={
						form.aboveMaxNotional
							? t("validation.max-order")
							: form.belowMinNotional
								? t("validation.min-order")
								: form.submitLabel
					}
					side={form.side}
					canSubmit={form.canSubmit}
					onSubmit={() => {
						placeOrder.mutate(form.orderParams);
						form.resetInputs();
					}}
				/>
			)}
		</div>
	);
}
